import { useLingui } from '@lingui/react/macro'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  getSystemDocumentBqId,
  getSystemDocumentDisplayTitle,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { resolveDocReferenceTargets } from 'features/knowledgeDocumentViewer/utils/docReferenceResolver'
import { resolveDerivedPageImageFileRef } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import {
  allCandidatesHaveRevisionCodes,
  getLatestCandidateByRevision,
  type NavigationTargetCandidate,
} from 'features/knowledgeDocumentViewer/utils/navigationTargetResolver'
import { useRolodexSignedURLs } from 'features/rolodex'
import { useMemo } from 'react'
import styled from 'styled-components'

const CARD_WIDTH = 264
const CURSOR_OFFSET = 16

const Card = styled.div`
  position: fixed;
  z-index: 1000;
  width: ${CARD_WIDTH}px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.55rem 0.6rem;
  border-radius: 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.35);
  font-size: 0.78rem;
`

const Title = styled.div`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Thumb = styled.img`
  width: 100%;
  max-height: 170px;
  object-fit: contain;
  border-radius: 0.3rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background: #fff;
`

const ThumbFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 90px;
  border-radius: 0.3rem;
  border: 1px dashed ${({ theme }) => theme.forecast.border};
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Muted = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Footer = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.7rem;
`

interface DocReferencePreviewCardProps {
  referenceLabel: string
  /** Candidate texts to resolve, in priority order (canonical tag → label). */
  resolutionTexts: string[]
  currentDocumentId: string
  /** The hover-primed string-match document set the click resolver also uses. */
  systemDocumentEntries: SystemDocumentEntry[]
  isLoadingEntries: boolean
  siteId: string
  /** Cursor position the hover started at, in viewport coordinates. */
  position: { x: number; y: number }
}

/**
 * Hover preview for a doc-reference box: the page the click would land on,
 * resolved through the same target chain as {@link resolveDocReferenceTargets}
 * so the preview can never disagree with the navigation.
 */
export function DocReferencePreviewCard({
  referenceLabel,
  resolutionTexts,
  currentDocumentId,
  systemDocumentEntries,
  isLoadingEntries,
  siteId,
  position,
}: DocReferencePreviewCardProps) {
  const { t } = useLingui()
  const resolution = useMemo(() => {
    let targets: NavigationTargetCandidate[] = []
    for (const text of resolutionTexts) {
      targets = resolveDocReferenceTargets({
        referenceText: text,
        currentDocumentId,
        systemDocumentEntries,
      })
      if (targets.length > 0) break
    }
    const best =
      targets.length > 1 && allCandidatesHaveRevisionCodes(targets)
        ? (getLatestCandidateByRevision(targets) ?? targets[0])
        : targets[0]
    return { targets, best }
  }, [resolutionTexts, currentDocumentId, systemDocumentEntries])

  const targetEntry = useMemo(
    () =>
      resolution.best &&
      systemDocumentEntries.find(
        (document) =>
          getSystemDocumentBqId(document) === resolution.best?.documentId
      ),
    [resolution.best, systemDocumentEntries]
  )

  const thumbnailRef = useMemo(
    () =>
      resolution.best &&
      resolveDerivedPageImageFileRef({
        siteId,
        bqDocumentId: resolution.best.documentId,
        pageNumber: resolution.best.pageNumber,
        variant: 'thumbnail',
      }),
    [resolution.best, siteId]
  )
  const { url, isLoading: isUrlLoading } = useRolodexSignedURLs(
    thumbnailRef || undefined
  )

  const title =
    (targetEntry && getSystemDocumentDisplayTitle(targetEntry)) ||
    t`Referenced document`

  const matchCount = resolution.targets.length
  const footer =
    matchCount <= 1
      ? t`Click to open`
      : allCandidatesHaveRevisionCodes(resolution.targets)
        ? t`Latest of ${matchCount} revisions — click to open`
        : t`${matchCount} matching documents — click to choose`
  const bestPageNumber = resolution.best?.pageNumber

  const left = Math.min(
    position.x + CURSOR_OFFSET,
    window.innerWidth - CARD_WIDTH - 12
  )
  const top = Math.min(position.y + CURSOR_OFFSET, window.innerHeight - 250)

  return (
    <Card style={{ left, top }}>
      {isLoadingEntries ? (
        <Muted>{t`Resolving “${referenceLabel}”…`}</Muted>
      ) : !resolution.best ? (
        <Muted>{t`No document found for “${referenceLabel}”`}</Muted>
      ) : (
        <>
          <Title title={title}>{title}</Title>
          {url ? (
            <Thumb src={url} alt={t`Page ${bestPageNumber} of ${title}`} />
          ) : (
            <ThumbFallback>
              {isUrlLoading ? t`Loading preview…` : t`No preview available`}
            </ThumbFallback>
          )}
          <Footer>{footer}</Footer>
        </>
      )}
    </Card>
  )
}
